import { z } from 'zod';

import { imageUrlSchema, slugSchema } from './store';

/**
 * Torneios (docs/torneios), entre a site-api, o site e o back-office. Datas
 * em ISO-8601 (UTC); o site e o back-office mostram-nas em hora de Lisboa.
 *
 * Prefixos HTTP:
 *   - `/api/tournaments/...`       site publico (paginas e inscricoes);
 *   - `/api/site/tournaments/...`  back-office (`tournaments.manage`), so em gestao.
 *
 * Parte 1: o torneio e as inscricoes. Individual (cada conta por si; os jovens
 * pelo encarregado) ou de equipa (so o nome da equipa). Cada torneio escolhe
 * quem se pode inscrever. Acima do maximo fica-se em lista de espera e sobe-se
 * pela ordem de chegada.
 */

/** `draft` so no back-office; `cancelled` continua visivel com o motivo. */
export const tournamentStatusSchema = z.enum(['draft', 'published', 'cancelled']);
export type TournamentStatus = z.infer<typeof tournamentStatusSchema>;

/** Inscricoes: `not_yet` abrem em `registrationOpensAt`; `closed` fecharam. */
export const tournamentWindowSchema = z.enum(['not_yet', 'open', 'closed']);
export type TournamentWindow = z.infer<typeof tournamentWindowSchema>;

/** `everyone` todas as contas; `members` membros e associados; `associates` so associados. */
export const tournamentAudienceSchema = z.enum(['everyone', 'members', 'associates']);
export type TournamentAudience = z.infer<typeof tournamentAudienceSchema>;

/** `solo` individual; `team` de equipa (na 1.a fase, so o nome da equipa). */
export const participantTypeSchema = z.enum(['solo', 'team']);
export type ParticipantType = z.infer<typeof participantTypeSchema>;

/** Formato: eliminacao simples, ou fase de grupos (round-robin) e depois playoffs (parte 4). */
export const tournamentFormatSchema = z.enum(['single_elimination', 'groups_playoffs']);
export type TournamentFormat = z.infer<typeof tournamentFormatSchema>;

/** Check-in: `null` sem check-in; `not_yet` abre em `checkInOpensAt`; `closed` com o bracket feito. */
export const checkInWindowSchema = z.enum(['not_yet', 'open', 'closed']);
export type CheckInWindow = z.infer<typeof checkInWindowSchema>;

export const MIN_CHECKIN_MINUTES = 5;
export const MAX_CHECKIN_MINUTES = 240;

export const bestOfSchema = z.union([z.literal(1), z.literal(3), z.literal(5), z.literal(7)]);
export type BestOf = z.infer<typeof bestOfSchema>;

/** Grupos + playoffs: quantos grupos e quantos passam de cada um. */
export const groupCountSchema = z.union([z.literal(2), z.literal(4), z.literal(8)]);
export type GroupCount = z.infer<typeof groupCountSchema>;
export const advancePerGroupSchema = z.union([z.literal(1), z.literal(2)]);
export type AdvancePerGroup = z.infer<typeof advancePerGroupSchema>;

/** Minimo de participantes para os grupos: em cada grupo fica pelo menos um de fora. */
export const minForGroups = (groupCount: number, advancePerGroup: number) =>
  groupCount * (advancePerGroup + 1);

export const tournamentEntryStatusSchema = z.enum(['confirmed', 'waitlisted']);
export type TournamentEntryStatus = z.infer<typeof tournamentEntryStatusSchema>;

export const MIN_PARTICIPANTS = 2;
export const MAX_PARTICIPANTS = 128;
export const MAX_TOURNAMENT_TEXT = 20_000;

const datetimeSchema = z.string().datetime({ offset: true });

/** Nome de equipa ou de participante: 1 a 60 caracteres, espacos aparados. */
export const entryNameSchema = z
  .string()
  .trim()
  .min(1, 'indique o nome')
  .max(60, 'no maximo 60 caracteres')
  .transform((s) => s.replace(/\s+/g, ' '));

// ---------------------------------------------------------------------------
// Bracket (parte 2)
// ---------------------------------------------------------------------------

/** Um lado de um jogo: quem joga e a cabeca de serie. */
export const bracketSlotSchema = z.object({
  entryId: z.string().uuid(),
  name: z.string(),
  seed: z.number().int(),
});
export type BracketSlot = z.infer<typeof bracketSlotSchema>;

export const bracketMatchSchema = z.object({
  id: z.string().uuid(),
  round: z.number().int(),
  position: z.number().int(),
  /** `null` = ainda por decidir (ou lugar vazio num bye). */
  a: bracketSlotSchema.nullable(),
  b: bracketSlotSchema.nullable(),
  winner: z.enum(['a', 'b']).nullable(),
  /** Um lado vazio desde o inicio: o outro passou sem jogar. */
  bye: z.boolean(),
  /** Resultado (parte 3); `null` sem resultado ou numa vitoria por falta. */
  scoreA: z.number().int().nullable(),
  scoreB: z.number().int().nullable(),
  /** Vitoria por falta (W.O.): desistencia, desqualificacao ou nao compareceu. */
  forfeit: z.boolean(),
  /** Empate (so nos grupos, num melhor de 1): tem resultado e nenhum vencedor. */
  draw: z.boolean(),
  /** Melhor de quantos (1, 3, 5 ou 7). */
  bestOf: z.number().int(),
  /** Hora marcada pelo staff (parte 5); `null` = sem hora. */
  scheduledAt: z.string().nullable(),
});
export type BracketMatch = z.infer<typeof bracketMatchSchema>;

/** Uma linha da tabela de um grupo, ja ordenada (parte 4). */
export const groupStandingSchema = z.object({
  entryId: z.string().uuid(),
  name: z.string(),
  seed: z.number().int(),
  played: z.number().int(),
  won: z.number().int(),
  drawn: z.number().int(),
  lost: z.number().int(),
  scoreFor: z.number().int(),
  scoreAgainst: z.number().int(),
  points: z.number().int(),
  /** Nos lugares que passam aos playoffs. */
  qualifying: z.boolean(),
  disqualified: z.boolean(),
});
export type GroupStanding = z.infer<typeof groupStandingSchema>;

export const tournamentGroupSchema = z.object({
  /** 0 = A. */
  group: z.number().int(),
  name: z.string(),
  /** Todos os jogos do grupo tem resultado. */
  complete: z.boolean(),
  standings: z.array(groupStandingSchema),
  /** Jornadas: "Jornada 1", "Jornada 2"... */
  rounds: z.array(
    z.object({ round: z.number().int(), title: z.string(), matches: z.array(bracketMatchSchema) }),
  ),
});
export type TournamentGroup = z.infer<typeof tournamentGroupSchema>;

export const bracketSchema = z.object({
  /** Fase de grupos (vazia na eliminacao simples). */
  groups: z.array(tournamentGroupSchema),
  /** Eliminacao (nos grupos + playoffs, vazia ate o staff gerar os playoffs). */
  rounds: z.array(
    z.object({ round: z.number().int(), title: z.string(), matches: z.array(bracketMatchSchema) }),
  ),
  /** O jogo de 3.o e 4.o lugar, se o torneio o tiver. */
  thirdPlace: bracketMatchSchema.nullable(),
  generatedAt: z.string(),
  /** A final (e o 3.o lugar, se houver) ja tem resultado. */
  finished: z.boolean(),
  /** Classificacao final (vazia ate terminar); lugares iguais empatam. */
  standings: z.array(z.object({ rank: z.number().int(), name: z.string() })),
});
export type Bracket = z.infer<typeof bracketSchema>;

// ---------------------------------------------------------------------------
// Site publico
// ---------------------------------------------------------------------------

export const publicTournamentSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  game: z.string(),
  summary: z.string(),
  description: z.string(),
  rules: z.string(),
  imageUrl: z.string().nullable(),
  participantType: participantTypeSchema,
  audience: tournamentAudienceSchema,
  maxParticipants: z.number().int(),
  online: z.boolean(),
  venue: z.string(),
  startsAt: z.string(),
  registrationOpensAt: z.string(),
  registrationClosesAt: z.string(),
  format: tournamentFormatSchema,
  groupCount: z.number().int(),
  advancePerGroup: z.number().int(),
  thirdPlace: z.boolean(),
  checkIn: checkInWindowSchema.nullable(),
  checkInOpensAt: z.string().nullable(),
  status: z.enum(['published', 'cancelled']),
  cancelReason: z.string().nullable(),
  window: tournamentWindowSchema,
  /** Os confirmados, pela ordem de inscricao (so o nome). */
  participants: z.array(z.string()),
  waitlisted: z.number().int(),
  /** O bracket ja foi gerado (as inscricoes fecharam). */
  started: z.boolean(),
  /** So na pagina de um torneio (na lista vem `null`). */
  bracket: bracketSchema.nullable(),
});
export type PublicTournament = z.infer<typeof publicTournamentSchema>;

/** Porque uma conta (ou um jovem) nao se pode inscrever agora. */
export const tournamentBlockSchema = z.enum([
  'associates_only',
  'members_only',
  'not_open_yet',
  'closed',
]);
export type TournamentBlock = z.infer<typeof tournamentBlockSchema>;

/** Eu ou um jovem meu, neste torneio. */
export const tournamentPersonSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  self: z.boolean(),
  /** `null` = nao inscrito. */
  entry: z
    .object({
      name: z.string(),
      status: tournamentEntryStatusSchema,
      /** Lugar na lista de espera (1 = o proximo); `null` se confirmado. */
      waitlistPosition: z.number().int().nullable(),
      checkedIn: z.boolean(),
    })
    .nullable(),
  /** `null` = pode inscrever-se (ou sair) agora. */
  blocked: tournamentBlockSchema.nullable(),
});
export type TournamentPerson = z.infer<typeof tournamentPersonSchema>;

/** `GET /api/tournaments/:slug/me`. */
export const tournamentMeSchema = z.object({ people: z.array(tournamentPersonSchema) });
export type TournamentMe = z.infer<typeof tournamentMeSchema>;

/**
 * `POST /api/tournaments/:slug/entries`. Sem `youthAccountId` = a propria
 * conta. `teamName` so (e sempre) nos torneios de equipa.
 */
export const tournamentEntryRequestSchema = z.object({
  youthAccountId: z.string().uuid().nullable().default(null),
  teamName: entryNameSchema.nullable().default(null),
});
export type TournamentEntryRequest = z.infer<typeof tournamentEntryRequestSchema>;

/** `POST /api/tournaments/:slug/withdraw`. */
export const tournamentWithdrawRequestSchema = z.object({
  youthAccountId: z.string().uuid().nullable().default(null),
});
export type TournamentWithdrawRequest = z.infer<typeof tournamentWithdrawRequestSchema>;

/** `POST /api/tournaments/:slug/check-in`: fazer check-in (a propria conta ou um jovem). */
export const tournamentCheckInRequestSchema = tournamentWithdrawRequestSchema;

// ---------------------------------------------------------------------------
// Back-office
// ---------------------------------------------------------------------------

export const tournamentInputSchema = z
  .object({
    slug: slugSchema,
    title: z.string().trim().min(1).max(120),
    game: z.string().trim().min(1).max(80),
    summary: z.string().trim().max(300).default(''),
    description: z.string().trim().max(MAX_TOURNAMENT_TEXT).default(''),
    rules: z.string().trim().max(MAX_TOURNAMENT_TEXT).default(''),
    imageUrl: imageUrlSchema,
    participantType: participantTypeSchema.default('solo'),
    audience: tournamentAudienceSchema.default('everyone'),
    maxParticipants: z.number().int().min(MIN_PARTICIPANTS).max(MAX_PARTICIPANTS).default(16),
    online: z.boolean().default(false),
    /** O local (presencial) ou a plataforma (online). */
    venue: z.string().trim().max(200).default(''),
    startsAt: datetimeSchema,
    /** `null` = abrem ao publicar. */
    registrationOpensAt: datetimeSchema.nullable().default(null),
    /** `null` = fecham quando o torneio comeca. */
    registrationClosesAt: datetimeSchema.nullable().default(null),
    format: tournamentFormatSchema.default('single_elimination'),
    /** So nos grupos + playoffs: 2, 4 ou 8 grupos. */
    groupCount: groupCountSchema.default(2),
    /** So nos grupos + playoffs: passam 1 ou 2 de cada grupo. */
    advancePerGroup: advancePerGroupSchema.default(2),
    /** Jogo de 3.o e 4.o lugar entre os vencidos das meias-finais. */
    thirdPlace: z.boolean().default(false),
    /** Melhor de 1, 3, 5 ou 7. */
    bestOf: bestOfSchema.default(1),
    /** A final pode ser diferente; `null` = igual aos outros. */
    finalBestOf: bestOfSchema.nullable().default(null),
    /** Minutos antes do inicio em que o check-in abre; `null` = sem check-in. */
    checkInMinutes: z
      .number()
      .int()
      .min(MIN_CHECKIN_MINUTES)
      .max(MAX_CHECKIN_MINUTES)
      .nullable()
      .default(null),
  })
  .refine(
    (t) =>
      t.registrationOpensAt === null ||
      t.registrationClosesAt === null ||
      new Date(t.registrationClosesAt) > new Date(t.registrationOpensAt),
    { message: 'tem de fechar depois de abrir', path: ['registrationClosesAt'] },
  )
  .refine(
    (t) =>
      t.registrationClosesAt === null || new Date(t.registrationClosesAt) <= new Date(t.startsAt),
    { message: 'as inscricoes fecham ate ao inicio do torneio', path: ['registrationClosesAt'] },
  )
  .refine(
    (t) =>
      t.format !== 'groups_playoffs' ||
      t.maxParticipants >= minForGroups(t.groupCount, t.advancePerGroup),
    {
      message:
        'poucos lugares para estes grupos: cada grupo precisa de mais participantes do que os que passam',
      path: ['maxParticipants'],
    },
  );
export type TournamentInput = z.infer<typeof tournamentInputSchema>;

export const adminTournamentSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  game: z.string(),
  summary: z.string(),
  description: z.string(),
  rules: z.string(),
  imageUrl: z.string().nullable(),
  participantType: participantTypeSchema,
  audience: tournamentAudienceSchema,
  maxParticipants: z.number().int(),
  online: z.boolean(),
  venue: z.string(),
  startsAt: z.string(),
  registrationOpensAt: z.string().nullable(),
  registrationClosesAt: z.string().nullable(),
  status: tournamentStatusSchema,
  window: tournamentWindowSchema,
  publishedAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  format: tournamentFormatSchema,
  groupCount: z.number().int(),
  advancePerGroup: z.number().int(),
  thirdPlace: z.boolean(),
  checkInMinutes: z.number().int().nullable(),
  checkIn: checkInWindowSchema.nullable(),
  bracketGeneratedAt: z.string().nullable(),
  bestOf: z.number().int(),
  finalBestOf: z.number().int().nullable(),
  finishedAt: z.string().nullable(),
  /** Muda a cada alteracao (resultados, desistencias): para recarregar o bracket. */
  updatedAt: z.string(),
  confirmed: z.number().int(),
  waitlisted: z.number().int(),
  checkedIn: z.number().int(),
  createdAt: z.string(),
});
export type AdminTournament = z.infer<typeof adminTournamentSchema>;

export const adminTournamentEntrySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  status: tournamentEntryStatusSchema,
  /** A conta que joga (login); `null` = juntado pelo staff so com o nome. */
  accountLogin: z.string().nullable(),
  /** Quem inscreveu: a propria conta, o encarregado ou o staff. */
  enteredByLogin: z.string(),
  checkedIn: z.boolean(),
  /** Cabeca de serie no bracket; `null` = nao joga (ou ainda nao ha bracket). */
  seed: z.number().int().nullable(),
  /** Desistiu ou foi desqualificado (parte 3): os jogos seguintes dao W.O. */
  disqualified: z.boolean(),
  disqualifyReason: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminTournamentEntry = z.infer<typeof adminTournamentEntrySchema>;

/** `POST /api/site/tournaments/:id/entries`: o staff junta alguem so com o nome. */
export const addTournamentEntryRequestSchema = z.object({ name: entryNameSchema });

export const cancelTournamentRequestSchema = z.object({
  reason: z.string().trim().min(1).max(300),
});

/** `POST /api/site/tournaments/:id/entries/:entryId/check-in`. */
export const staffCheckInRequestSchema = z.object({ checkedIn: z.boolean() });

/**
 * Como ordenar as cabecas de serie: `random` ao acaso; `registration` pela
 * ordem de inscricao; `manual` pela ordem dada em `order` (todos os que jogam).
 */
export const seedingSchema = z.enum(['random', 'registration', 'manual']);
export type Seeding = z.infer<typeof seedingSchema>;

/** `POST /api/site/tournaments/:id/bracket`. */
export const generateBracketRequestSchema = z
  .object({
    seeding: seedingSchema,
    order: z.array(z.string().uuid()).max(MAX_PARTICIPANTS).nullable().default(null),
  })
  .refine((r) => r.seeding !== 'manual' || r.order !== null, {
    message: 'indique a ordem',
    path: ['order'],
  });
export type GenerateBracketRequest = z.infer<typeof generateBracketRequestSchema>;

/**
 * `POST /api/site/tournaments/:id/playoffs`: gera os playoffs a partir das
 * tabelas dos grupos (sem corpo); `DELETE` refaz enquanto nao houver resultados.
 */

/** `GET /api/site/tournaments/:id/bracket`: quem joga agora e o bracket, se ja houver. */
export const adminBracketSchema = z.object({
  /** Quem entraria no bracket se fosse gerado agora, por ordem de inscricao. */
  eligible: z.array(z.object({ entryId: z.string().uuid(), name: z.string() })),
  bracket: bracketSchema.nullable(),
});
export type AdminBracket = z.infer<typeof adminBracketSchema>;

/**
 * `PUT /api/site/tournaments/:id/matches/:matchId`: o resultado, ou uma
 * vitoria por falta (`forfeitWinner`, sem resultado).
 */
export const matchResultRequestSchema = z
  .object({
    scoreA: z.number().int().min(0).max(999).nullable().default(null),
    scoreB: z.number().int().min(0).max(999).nullable().default(null),
    forfeitWinner: z.enum(['a', 'b']).nullable().default(null),
  })
  .refine(
    (r) =>
      r.forfeitWinner !== null
        ? r.scoreA === null && r.scoreB === null
        : r.scoreA !== null && r.scoreB !== null,
    { message: 'indique o resultado ou quem ganha por falta', path: ['scoreA'] },
  );
export type MatchResultRequest = z.infer<typeof matchResultRequestSchema>;

/** `PUT /api/site/tournaments/:id/matches/:matchId/schedule`: a hora do jogo (`null` tira). */
export const matchScheduleRequestSchema = z.object({
  scheduledAt: datetimeSchema.nullable(),
});
export type MatchScheduleRequest = z.infer<typeof matchScheduleRequestSchema>;

/** `POST /api/site/tournaments/:id/entries/:entryId/disqualify`: desistiu ou desqualificado. */
export const disqualifyRequestSchema = z.object({
  reason: z.string().trim().min(1).max(200),
});
